import { valibotResolver } from "@hookform/resolvers/valibot";
import { type JSX, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { TemplateEditor, TemplatePreview } from "common/components/Templates";
import { TEMPLATE_TYPES } from "common/constants";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { CreatedTemplate, Template } from "common/types";

import { useCampaignFlowContext } from "../../../context/CampaignFlowContext";
import useAudience from "../../../datahooks/useAudience";
import useDetectLanguage from "../../../datahooks/useDetectLanguage";
import DesktopLayout from "./DesktopLayout";
import { FlowTemplateProvider } from "./FlowTemplateContext";
import LanguageDropdown from "./LanguageDropdown";
import MobileLayout from "./MobileLayout";

import type {
  FlowInmailTemplate,
  FlowTemplate as FlowTemplateType,
} from "../../../types";

interface FlowTemplateProps {
  flowTemplate: FlowTemplateType;
  setFlowTemplate: (flowTemplate: FlowTemplateType) => void;
  handleRemove: () => void;
  isRemoveButtonHidden?: boolean;
  actionDelayComponent?: JSX.Element;
}

function getTemplateFromFlowTemplate(flowTemplate: FlowTemplateType): Template {
  return {
    type: flowTemplate.type,
    subject: "subject" in flowTemplate ? flowTemplate.subject : undefined,
    ai_descriptors: flowTemplate.ai_descriptors,
    name: "",
    message: flowTemplate.message,
  };
}

export default function FlowTemplate({
  flowTemplate,
  setFlowTemplate,
  handleRemove,
  isRemoveButtonHidden = false,
  actionDelayComponent = null,
}: FlowTemplateProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isEditing, setIsEditing] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template>(
    flowTemplate.original_id
      ? getTemplateFromFlowTemplate(flowTemplate)
      : undefined,
  );

  const { detectLanguage } = useDetectLanguage();
  const { audience } = useAudience();
  const {
    id,
    owners,
    custom_variable_keys: customVariables,
  } = useCampaignContext();
  const { resetFlowError, flow } = useCampaignFlowContext();
  const { accounts } = useSelectedWorkspaceContext();

  const { type, message, ai_descriptors: aiDescriptors } = flowTemplate;
  const { flowValidationSchema } = TEMPLATE_TYPES[type];

  const form = useForm<Template>({
    defaultValues: {
      type,
      message,
      subject: type === "INMAIL_TEMPLATE" ? flowTemplate.subject : undefined,
      ai_descriptors: aiDescriptors,
    },
    resolver: valibotResolver(flowValidationSchema),
  });

  function handleSelectTemplate(selectedTemplate: CreatedTemplate) {
    setPreviewTemplate(selectedTemplate);

    const {
      created_at: createdAt,
      metrics,
      campaigns,
      ...rest
    } = selectedTemplate;

    form.reset(rest);

    const finalFlowTemplate: FlowTemplateType = {
      ...flowTemplate,
      original_id: selectedTemplate.id,
      edited: false,
      message: selectedTemplate.message,
      ai_descriptors: selectedTemplate.ai_descriptors,
    };

    if (selectedTemplate.type === "INMAIL_TEMPLATE") {
      (finalFlowTemplate as FlowInmailTemplate).subject =
        selectedTemplate.subject;
    }

    setFlowTemplate(finalFlowTemplate);

    if (!flow.is_manual_language) {
      detectLanguage({ text: finalFlowTemplate.message });
    }

    // Remove from invalidFlows
    if (resetFlowError) {
      resetFlowError();
    }
  }

  function onSubmit(data: Template) {
    setFlowTemplate({
      ...flowTemplate,
      ...data,
      edited: true,
    } as FlowTemplateType);

    form.reset({
      ...getTemplateFromFlowTemplate(flowTemplate),
      ...data,
    });
    setPreviewTemplate({
      ...getTemplateFromFlowTemplate(flowTemplate),
      ...data,
    });

    detectLanguage({ text: data.message });
    setIsEditing(false);
  }

  function handleDiscard() {
    form.reset(getTemplateFromFlowTemplate(flowTemplate));
    setIsEditing(false);
  }

  function handleSave() {
    form.handleSubmit(onSubmit)();
  }

  function handleStartEdit() {
    setIsEditing(true);
  }

  const contextValue = useMemo(
    () => ({
      flowTemplate,
      isRemoveButtonHidden,
      form,
      isEditing,
      handleDiscard,
      handleSelectTemplate,
      handleSave,
      handleStartEdit,
      handleRemove,
    }),
    [
      flowTemplate,
      isRemoveButtonHidden,
      form,
      isEditing,
      handleDiscard,
      handleSelectTemplate,
      handleSave,
      handleStartEdit,
      handleRemove,
    ],
  );

  const targetProfiles = audience?.slice(0, 3);
  const updatedTemplate = form.watch();

  const templatePreviewComponent = (
    <TemplatePreview
      previewTemplate={previewTemplate}
      updatedTemplate={updatedTemplate}
      onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
      targetProfiles={targetProfiles}
      senderProfiles={accounts.filter(({ id }) => owners.includes(id))}
      campaignData={{
        campaignId: id,
        sourceLanguageCode: flow.source_language,
        isTranslateEnabled: flow.translate,
      }}
    />
  );

  const templateEditorComponent = (
    <TemplateEditor
      key={
        flowTemplate
          ? `${flowTemplate.original_id}-${isEditing}`
          : "no-template"
      }
      form={form}
      isEditable={isEditing}
      customVariables={customVariables}
      autoFocus
      placeholderText={
        !flowTemplate &&
        "Select a template first. The text will show a preview here when selected"
      }
      languageDropdownComponent={<LanguageDropdown />}
    />
  );

  const Component = isDesktop ? DesktopLayout : MobileLayout;

  return (
    <FlowTemplateProvider value={contextValue}>
      <Component
        actionDelayComponent={actionDelayComponent}
        templatePreviewComponent={templatePreviewComponent}
        templateEditorComponent={templateEditorComponent}
      />
    </FlowTemplateProvider>
  );
}
